import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

interface Device {
  id: string;
  deviceId: string;
  deviceName: string;
  deviceType: 'dartboard' | 'tablet' | 'mobile';
  ownerId: string;
  ownerName: string;
  venueId?: string;
  venueName?: string;
  pinHash: string;
  recoveryCode: string;
  status: 'active' | 'locked' | 'stolen' | 'lost';
  lastSeen: Date;
  registeredAt: Date;
}

const DeviceRegistry: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAdmin, isVenueOperator } = useAuth();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Device | null>(null);
  const [form, setForm] = useState({
    deviceId: '',
    deviceName: '',
    deviceType: 'dartboard' as Device['deviceType'],
    venueId: '',
    venueName: '',
    pin: '',
    confirmPin: ''
  });

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      setLoading(true);
      let q;
      
      if (isAdmin) {
        q = query(collection(db, 'devices'));
      } else if (isVenueOperator) {
        q = query(collection(db, 'devices'), where('ownerId', '==', user?.uid));
      } else {
        q = query(collection(db, 'devices'), where('ownerId', '==', user?.uid));
      }
      
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastSeen: doc.data().lastSeen?.toDate(),
        registeredAt: doc.data().registeredAt?.toDate()
      })) as Device[];
      
      setDevices(data);
    } catch (error) {
      console.error('Error loading devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateRecoveryCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 16; i++) {
      if (i > 0 && i % 4 === 0) code += '-';
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const hashPin = (pin: string) => {
    // Simple hash for demo - in production use proper crypto
    return btoa(pin);
  };

  const handleSave = async () => {
    if (form.pin !== form.confirmPin) {
      alert(t('deviceLock.pinMismatch'));
      return;
    }

    if (form.pin.length !== 6) {
      alert(t('deviceLock.pinLength'));
      return;
    }

    try {
      const deviceData = {
        deviceId: form.deviceId,
        deviceName: form.deviceName,
        deviceType: form.deviceType,
        ownerId: user?.uid || '',
        ownerName: user?.displayName || user?.email || '',
        venueId: form.venueId || null,
        venueName: form.venueName || null,
        pinHash: hashPin(form.pin),
        recoveryCode: editing ? editing.recoveryCode : generateRecoveryCode(),
        status: 'active' as Device['status'],
        lastSeen: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      if (editing) {
        await updateDoc(doc(db, 'devices', editing.id), deviceData);
      } else {
        await addDoc(collection(db, 'devices'), {
          ...deviceData,
          registeredAt: Timestamp.now()
        });
      }

      setShowModal(false);
      setEditing(null);
      resetForm();
      loadDevices();
    } catch (error) {
      console.error('Error saving device:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('deviceLock.confirmDelete'))) {
      try {
        await deleteDoc(doc(db, 'devices', id));
        loadDevices();
      } catch (error) {
        console.error('Error deleting device:', error);
      }
    }
  };

  const handleStatusChange = async (device: Device, newStatus: Device['status']) => {
    try {
      await updateDoc(doc(db, 'devices', device.id), {
        status: newStatus,
        updatedAt: Timestamp.now()
      });
      loadDevices();
    } catch (error) {
      console.error('Error updating device status:', error);
    }
  };

  const handleEdit = (device: Device) => {
    setEditing(device);
    setForm({
      deviceId: device.deviceId,
      deviceName: device.deviceName,
      deviceType: device.deviceType,
      venueId: device.venueId || '',
      venueName: device.venueName || '',
      pin: '',
      confirmPin: ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      deviceId: '',
      deviceName: '',
      deviceType: 'dartboard',
      venueId: '',
      venueName: '',
      pin: '',
      confirmPin: ''
    });
  };

  const getStatusColor = (status: Device['status']) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'locked': return 'bg-yellow-500/20 text-yellow-400';
      case 'stolen': return 'bg-red-500/20 text-red-400';
      case 'lost': return 'bg-orange-500/20 text-orange-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getDeviceIcon = (type: Device['deviceType']) => {
    switch (type) {
      case 'dartboard': return '🎯';
      case 'tablet': return '📱';
      case 'mobile': return '📲';
      default: return '🔧';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-white">{t('deviceLock.deviceRegistry')}</h3>
        <button
          onClick={() => {
            resetForm();
            setEditing(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('deviceLock.registerDevice')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {devices.map(device => (
          <div key={device.id} className="card-dark p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{getDeviceIcon(device.deviceType)}</span>
                <div>
                  <h4 className="text-lg font-bold text-white">{device.deviceName}</h4>
                  <p className="text-xs text-[#B8B8C3]">{device.deviceId}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(device.status)}`}>
                {t(`deviceLock.status.${device.status}`)}
              </span>
            </div>

            <div className="space-y-2 mb-4">
              <div className="text-sm">
                <span className="text-[#B8B8C3]">{t('deviceLock.owner')}:</span>
                <p className="text-white">{device.ownerName}</p>
              </div>
              
              {device.venueName && (
                <div className="text-sm">
                  <span className="text-[#B8B8C3]">{t('deviceLock.venue')}:</span>
                  <p className="text-white">{device.venueName}</p>
                </div>
              )}

              <div className="text-sm">
                <span className="text-[#B8B8C3]">{t('deviceLock.lastSeen')}:</span>
                <p className="text-white">{device.lastSeen?.toLocaleString()}</p>
              </div>

              <div className="text-sm">
                <span className="text-[#B8B8C3]">{t('deviceLock.recoveryCode')}:</span>
                <p className="text-cyan-400 font-mono text-xs">{device.recoveryCode}</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(device)}
                  className="flex-1 btn-secondary text-sm"
                >
                  {t('common.edit')}
                </button>
                <button
                  onClick={() => handleDelete(device.id)}
                  className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
                >
                  {t('common.delete')}
                </button>
              </div>

              {device.status === 'active' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleStatusChange(device, 'locked')}
                    className="flex-1 px-3 py-2 bg-yellow-500/10 text-yellow-400 rounded-lg hover:bg-yellow-500/20 transition-colors text-xs"
                  >
                    🔒 {t('deviceLock.lock')}
                  </button>
                  <button
                    onClick={() => handleStatusChange(device, 'stolen')}
                    className="flex-1 px-3 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-xs"
                  >
                    🚨 {t('deviceLock.reportStolen')}
                  </button>
                </div>
              )}

              {(device.status === 'locked' || device.status === 'stolen' || device.status === 'lost') && (
                <button
                  onClick={() => handleStatusChange(device, 'active')}
                  className="w-full px-3 py-2 bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors text-xs"
                >
                  ✅ {t('deviceLock.unlock')}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {devices.length === 0 && (
        <div className="card-dark p-12 text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h3 className="text-xl font-bold text-white mb-2">{t('deviceLock.noDevices')}</h3>
          <p className="text-[#B8B8C3] mb-4">{t('deviceLock.noDevicesDesc')}</p>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn-primary"
          >
            {t('deviceLock.registerFirstDevice')}
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editing ? t('deviceLock.editDevice') : t('deviceLock.registerDevice')}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('deviceLock.deviceId')}
                </label>
                <input
                  type="text"
                  value={form.deviceId}
                  onChange={(e) => setForm({ ...form, deviceId: e.target.value })}
                  className="input-dark w-full"
                  placeholder="DQ-2024-001"
                  disabled={!!editing}
                />
                <p className="text-xs text-[#B8B8C3] mt-1">{t('deviceLock.deviceIdHint')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('deviceLock.deviceName')}
                </label>
                <input
                  type="text"
                  value={form.deviceName}
                  onChange={(e) => setForm({ ...form, deviceName: e.target.value })}
                  className="input-dark w-full"
                  placeholder={t('deviceLock.deviceNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('deviceLock.deviceType')}
                </label>
                <select
                  value={form.deviceType}
                  onChange={(e) => setForm({ ...form, deviceType: e.target.value as Device['deviceType'] })}
                  className="input-dark w-full"
                >
                  <option value="dartboard">{t('deviceLock.type.dartboard')}</option>
                  <option value="tablet">{t('deviceLock.type.tablet')}</option>
                  <option value="mobile">{t('deviceLock.type.mobile')}</option>
                </select>
              </div>

              {isAdmin && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                      {t('deviceLock.venueId')}
                    </label>
                    <input
                      type="text"
                      value={form.venueId}
                      onChange={(e) => setForm({ ...form, venueId: e.target.value })}
                      className="input-dark w-full"
                      placeholder="Optional"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                      {t('deviceLock.venueName')}
                    </label>
                    <input
                      type="text"
                      value={form.venueName}
                      onChange={(e) => setForm({ ...form, venueName: e.target.value })}
                      className="input-dark w-full"
                      placeholder="Optional"
                    />
                  </div>
                </div>
              )}

              <div className="border-t border-[#31383D] pt-4">
                <h4 className="text-lg font-bold text-white mb-4">
                  {editing ? t('deviceLock.changePIN') : t('deviceLock.setPIN')}
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                      {t('deviceLock.pin')}
                    </label>
                    <input
                      type="password"
                      value={form.pin}
                      onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                      className="input-dark w-full text-center text-2xl tracking-widest"
                      placeholder="••••••"
                      maxLength={6}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                      {t('deviceLock.confirmPin')}
                    </label>
                    <input
                      type="password"
                      value={form.confirmPin}
                      onChange={(e) => setForm({ ...form, confirmPin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                      className="input-dark w-full text-center text-2xl tracking-widest"
                      placeholder="••••••"
                      maxLength={6}
                    />
                  </div>
                </div>
                <p className="text-xs text-[#B8B8C3] mt-2">{t('deviceLock.pinHint')}</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSave} className="btn-primary flex-1">
                {t('common.save')}
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditing(null);
                  resetForm();
                }}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceRegistry;
